import { useEffect, useRef, useState, type FormEvent } from 'react';
import { APP_NAME, CRISIS_RESOURCES } from '@lessalone/shared';
import { ThemeToggle } from '../components/ThemeToggle';
import { fetchEligibility } from '../lib/api';
import { emailOtp, signIn } from '../lib/auth';
import { isNative } from '../lib/capacitor';
import { UnsupportedCommunity } from './UnsupportedCommunity';

// LocalStorage key for the in-flight OTP flow. On a native WebView
// the OS may destroy the activity when the user switches to their
// email app to copy the code — without persistence, returning to
// LessAlone drops them back at the email-entry form and they can't
// paste their code. We hydrate from this key on mount and clear it
// once sign-in succeeds (or the user requests a new code).
const OTP_STATE_KEY = 'lessalone:otp:pending';

interface PersistedOtpState {
  email: string;
  sentAt: number;
}

function loadPersistedOtp(): PersistedOtpState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(OTP_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedOtpState;
    // Code expires server-side after 10 minutes; give a tiny buffer so
    // a fresh resume after expiry doesn't keep the user staring at an
    // OTP form that can't possibly work.
    if (!parsed.email || Date.now() - parsed.sentAt > 10 * 60 * 1000) {
      window.localStorage.removeItem(OTP_STATE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedOtp(email: string) {
  try {
    window.localStorage.setItem(
      OTP_STATE_KEY,
      JSON.stringify({ email, sentAt: Date.now() } satisfies PersistedOtpState),
    );
  } catch {
    // localStorage may be unavailable (private mode); the in-memory
    // state still works as long as the WebView stays alive.
  }
}

function clearPersistedOtp() {
  try {
    window.localStorage.removeItem(OTP_STATE_KEY);
  } catch {
    // ignore
  }
}

/**
 * The marketing landing page shown before sign-in: the story, the evidence,
 * and the email magic-link sign-in.
 */
// Capacitor's WebView can't reliably handle a magic-link tap (Gmail
// opens it in Chrome, cookie lands in the wrong jar). So on a native
// shell we collect an email, send a 6-digit code, and let the user
// type it into the app. The web SPA keeps the magic-link button.
//
// Evaluated INSIDE the component (not at module-load) so we don't
// snapshot a `false` before Capacitor's runtime has initialized.
// A module-level snapshot caused a real bug where the APK rendered
// the web magic-link form and sign-in just hung.

/**
 * The warm-band collage. The redesign brief is explicit: use REAL, candid
 * photography here — NEVER AI-generated faces, because the brand promise is
 * "real humans, never AI" and synthetic faces would undercut it. Drop three
 * image URLs into WARM_PHOTOS (Unsplash / Pexels, free + commercial-OK;
 * art-direct atmospheric-over-literal: window light, hands on a mug, campus
 * at dusk) and the collage renders. Until then a finished editorial panel
 * stands in so the section never looks unfinished in production.
 */
const WARM_PHOTOS: Array<{ src: string; alt: string }> = [
  { src: '/warm/window.jpg', alt: 'Soft daylight through a window onto green leaves outside' },
  { src: '/warm/journal.jpg', alt: 'A student writing in a journal at an outdoor café table' },
  { src: '/warm/mug.jpg', alt: 'Hands cradling a warm cup of coffee in morning light' },
];

function WarmCollage() {
  if (WARM_PHOTOS.length >= 3) {
    return (
      <div className="collage">
        {WARM_PHOTOS.slice(0, 3).map((photo, i) => (
          <img
            key={photo.src}
            className={i === 0 ? 'collage-tall' : 'collage-short'}
            src={photo.src}
            alt={photo.alt}
            loading="lazy"
          />
        ))}
        <span className="collage-cap">Real, candid moments — not stock smiles.</span>
      </div>
    );
  }
  // Finished fallback (no empty photo boxes): a single evocative "letter,"
  // the brand's own artifact, until real photography is dropped in.
  return (
    <div className="warm-figure" aria-hidden="true">
      <span className="hero-letter-eyebrow">From your match</span>
      <span className="hero-letter-from">QuietGrove</span>
      <span className="hero-letter-body">
        “You said the first month is its own kind of quiet. That helped more than you know — I went
        to the film club. Thank you for not letting me sit in it alone.”
      </span>
      <span className="hero-letter-meta">Reading week · 9:03am</span>
    </div>
  );
}

export function Landing() {
  const USE_OTP = isNative();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unsupportedEmail, setUnsupportedEmail] = useState<string | null>(null);
  // OTP flow state — only used when USE_OTP is true. The `otp` field is
  // the 6-digit code the user types; `verifying` flips while the
  // server-side check is in flight.
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  // Restore an in-flight OTP flow if the app was killed/backgrounded
  // while the user went to copy their code. Only on native — the web
  // SPA uses magic links and doesn't need this.
  useEffect(() => {
    if (!USE_OTP) return;
    const restored = loadPersistedOtp();
    if (restored) {
      setEmail(restored.email);
      setSent(true);
      window.setTimeout(() => otpRef.current?.focus(), 80);
    }
  }, [USE_OTP]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Eligibility pre-check — if no community claims this email's domain,
    // don't burn a magic-link / OTP round-trip; surface the demand-capture
    // screen instead.
    const eligibility = await fetchEligibility(email);
    if (!eligibility.supported) {
      setBusy(false);
      setUnsupportedEmail(email);
      return;
    }

    if (USE_OTP) {
      // Mobile path: send the OTP code and switch the card to the
      // code-entry state. No URL is involved.
      const result = await emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
      setBusy(false);
      if (result.error) {
        setError(result.error.message ?? 'Could not send the sign-in code.');
        return;
      }
      setSent(true);
      savePersistedOtp(email);
      // Focus the code input on the next tick so the OS keyboard pops.
      window.setTimeout(() => otpRef.current?.focus(), 60);
      return;
    }

    // Web path: send the magic link. Preserve where the user was
    // trying to go (push-notif → expired session) so a click drops
    // them back into that route. Only honour an internal path.
    const candidate = window.location.pathname + window.location.search;
    const callbackURL = candidate.startsWith('/') && !candidate.startsWith('//') ? candidate : '/';
    const result = await signIn.magicLink({ email, name: email, callbackURL });
    setBusy(false);
    if (result.error) {
      setError(result.error.message ?? 'Could not send the sign-in link.');
    } else {
      setSent(true);
    }
  }

  async function verifyOtp(event: FormEvent) {
    event.preventDefault();
    if (verifying) return;
    setVerifying(true);
    setError(null);
    const result = await signIn.emailOtp({ email, otp });
    setVerifying(false);
    if (result.error) {
      setError(
        result.error.message ?? "That code didn't work. Check the digits or send a new one.",
      );
      // Clear the entry so the user can retype without selecting first.
      setOtp('');
      otpRef.current?.focus();
      return;
    }
    // Better-Auth set the session cookie. <App> picks up the new session
    // on its next render via useSession + a refresh of fetchMe.
    clearPersistedOtp();
    window.location.assign('/');
  }

  function resendOtp() {
    setOtp('');
    setSent(false);
    setError(null);
    clearPersistedOtp();
    window.setTimeout(() => emailRef.current?.focus(), 60);
  }

  function focusStart() {
    emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => emailRef.current?.focus(), 400);
  }

  // If the visitor's email isn't in any partnered community, the lead-
  // capture screen takes over the whole page. Back button rewinds them
  // to the marketing landing.
  if (unsupportedEmail) {
    return (
      <UnsupportedCommunity email={unsupportedEmail} onDismiss={() => setUnsupportedEmail(null)} />
    );
  }

  // On native (Capacitor), drop the entire marketing page — a phone
  // shell doesn't need a website's hero + evidence + CTAs to sell the
  // user; they already downloaded the app. Instead we show a
  // streamlined sign-in screen that feels like a real mobile app.
  if (USE_OTP) {
    return (
      <main className="native-landing">
        <div className="native-landing-inner">
          <div className="native-landing-mark" aria-hidden="true">
            <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
              <circle cx="40" cy="40" r="38" fill="none" stroke="currentColor" strokeWidth="0.6" />
              <circle cx="40" cy="40" r="32" fill="none" stroke="currentColor" strokeWidth="0.5" />
              <text
                x="40"
                y="48"
                textAnchor="middle"
                fontSize="22"
                fontStyle="italic"
                fill="currentColor"
                fontFamily="Fraunces, Georgia, serif"
              >
                ✦
              </text>
            </svg>
          </div>
          <h1 className="native-landing-wordmark">
            <em className="wordmark-accent">Less</em>Alone
          </h1>
          <p className="native-landing-lede">
            One conversation at a time, with a real student. Not a chatbot, not therapy — just
            someone on the other side.
          </p>

          <div className="native-landing-card">
            {sent ? (
              <>
                <h2>Type the code from your email</h2>
                <p className="muted">
                  We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
                </p>
                <form onSubmit={verifyOtp}>
                  <input
                    ref={otpRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={otp}
                    placeholder="123456"
                    aria-label="6-digit sign-in code"
                    className="otp-input"
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <button type="submit" disabled={verifying || otp.length !== 6}>
                    {verifying ? 'Signing in…' : 'Sign in'}
                  </button>
                  {error && <p className="error">{error}</p>}
                </form>
                <p className="note">
                  Didn't get it?{' '}
                  <button type="button" className="linklike" onClick={resendOtp}>
                    Send a new code
                  </button>
                  . Check your spam folder too.
                </p>
              </>
            ) : (
              <>
                <h2>Sign in with your university email</h2>
                <p className="muted">
                  One email, one code — there is no password. New here or returning, the same code
                  opens your account.
                </p>
                <form onSubmit={submit}>
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    value={email}
                    placeholder="you@university.edu"
                    aria-label="University email"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <button type="submit" disabled={busy}>
                    {busy ? 'Sending…' : 'Email me a sign-in code'}
                  </button>
                  {error && <p className="error">{error}</p>}
                </form>
              </>
            )}
          </div>

          <p className="native-landing-reassure">
            <span>18+ only</span>
            <span aria-hidden="true">·</span>
            <span>Real humans, never AI</span>
            <span aria-hidden="true">·</span>
            <span>Moderated</span>
          </p>

          <p className="native-landing-crisis">
            Not a crisis service. In an emergency, call or text{' '}
            <strong>{CRISIS_RESOURCES[0].name}</strong>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="landing">
      <nav className="lnav">
        <span className="wordmark">
          <em className="wordmark-accent">Less</em>Alone
        </span>
        <div className="lnav-actions">
          <a href="#how">How it works</a>
          <ThemeToggle />
          <button type="button" className="btn-sm" onClick={focusStart}>
            Sign in
          </button>
        </div>
      </nav>

      <header className="hero">
        <div className="hero-copy">
          <span className="kicker">Peer support · not a chatbot</span>
          <h1>
            Less alone, one <em>conversation</em> at a time.
          </h1>
          <p className="hero-lede">
            Some weeks the hardest part is having no one to tell. {APP_NAME} quietly pairs you with
            another real student — message when you can, no pressure, no performance.
          </p>
          <div className="reassure">
            <span>Free for students</span>
            <span>Real humans, never AI</span>
            <span>Adults 18+</span>
            <span>Moderated &amp; private</span>
          </div>
        </div>

        <div className="hero-card-stack">
          <div className="hero-card" id="start">
            {sent && USE_OTP ? (
              <>
                <h2>Type the code from your email</h2>
                <p className="muted">
                  We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
                </p>
                <form onSubmit={verifyOtp}>
                  <input
                    ref={otpRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={otp}
                    placeholder="123456"
                    aria-label="6-digit sign-in code"
                    className="otp-input"
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <button type="submit" disabled={verifying || otp.length !== 6}>
                    {verifying ? 'Signing in…' : 'Sign in'}
                  </button>
                  {error && <p className="error">{error}</p>}
                </form>
                <p className="note">
                  Didn't get it?{' '}
                  <button type="button" className="linklike" onClick={resendOtp}>
                    Send a new code
                  </button>
                  . Check your spam folder too.
                </p>
              </>
            ) : sent ? (
              <>
                <h2>Check your email</h2>
                <p className="muted">
                  If <strong>{email}</strong> qualifies, a sign-in link is on its way. It expires in
                  30 minutes.
                </p>
                <p className="note">
                  New or returning, that one link is all you need — it opens your account.
                </p>
              </>
            ) : (
              <>
                <h2>Sign in with your university email</h2>
                <p className="muted">
                  {USE_OTP
                    ? 'One email, one code — there is no password. New here or returning, the same code opens your account.'
                    : 'One email, one tap — there is no password. New here or returning, it is the same link.'}
                </p>
                <form onSubmit={submit}>
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    value={email}
                    placeholder="you@university.edu"
                    aria-label="University email"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <button type="submit" disabled={busy}>
                    {busy
                      ? 'Sending…'
                      : USE_OTP
                        ? 'Email me a sign-in code'
                        : 'Email me a sign-in link'}
                  </button>
                  {error && <p className="error">{error}</p>}
                </form>
                <p className="note">
                  Already have an account? {USE_OTP ? 'This code signs you' : 'This link signs you'}{' '}
                  straight into it — it never creates a duplicate. First time? You'll get a short
                  intro and an age check.
                </p>
              </>
            )}
          </div>
        </div>
      </header>

      {/* WARM HUMAN BAND — the missing human presence. */}
      <section className="warm">
        <div className="warm-inner">
          <div className="warm-copy">
            <span className="kicker">You're not the only one</span>
            <h2>
              Almost everyone feels this — <em>almost no one says it.</em>
            </h2>
            <p>
              More than half of students say they feel lonely. The quiet is common; admitting it out
              loud is the rare, brave part.
            </p>
            <p>
              {APP_NAME} gives that admission somewhere soft to land: one real person, on the other
              side, who gets it.
            </p>
          </div>
          <WarmCollage />
        </div>
      </section>

      {/* PROOF — condensed to a sage-vs-clay face-off. */}
      <section className="proof">
        <div className="section-head">
          <span className="kicker">Backed by research</span>
          <h2>Talking to a human moves the needle. A chatbot doesn't.</h2>
          <p>
            A University of British Columbia trial tested ways to ease student loneliness over two
            weeks. The result was blunt.
          </p>
        </div>
        <div className="versus">
          <div className="vcard win">
            <span className="vt">Paired with a real person</span>
            <div className="fig">≈9%</div>
            <p className="vl">drop in loneliness — the pairing itself was the active ingredient.</p>
          </div>
          <div className="vcard lose">
            <span className="vt">Given an AI chatbot</span>
            <div className="fig">≈2%</div>
            <p className="vl">about the same as doing nothing at all.</p>
          </div>
        </div>
        <p className="evidence-note">
          Li et al., 2026 · 300 first-year students. It's exactly why {APP_NAME} uses real people,
          full stop.
        </p>
      </section>

      {/* A look inside — a sample exchange on its own paper-deep band (the
          reference's section rhythm: warm + safety on surface bands, this on
          paper-deep, the rest on flat paper). */}
      <section className="inside" aria-label="What a match looks like">
        <div className="inside-inner">
          <div className="inside-head">
            <span className="kicker">A look inside</span>
            <h2>This is what a match looks like.</h2>
            <p>
              Two nicknames, no real names. Reply in the morning, after class, before bed — there
              is no clock and no read receipt forcing anyone's hand.
            </p>
          </div>
          <div className="exchange">
            <article className="bubble peer">
              <div className="bh">
                <strong>QuietGrove</strong>
                <span>Tue · 4:12pm</span>
              </div>
              <p>
                First week in this town and I haven't said more than five words to anyone. Is that
                normal here, or is it just me?
              </p>
            </article>
            <article className="bubble self">
              <div className="bh">
                <strong>You</strong>
                <span>Tue · 7:48pm</span>
              </div>
              <p>
                Honestly, same. The first month is its own kind of quiet. Coffee shop on 4th has a
                bulletin board with a film club, if that helps at all.
              </p>
            </article>
            <article className="bubble peer">
              <div className="bh">
                <strong>QuietGrove</strong>
                <span>Wed · 8:21am</span>
              </div>
              <p>It does. Going Thursday. Will report back.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="lsection" id="how">
        <div className="section-head">
          <span className="kicker">How it works</span>
          <h2>Four calm steps.</h2>
        </div>
        <div className="steps">
          <div className="step">
            <h3>Verify with your campus email</h3>
            <p>
              Sign in with your university address — that is what keeps the community real and
              adults-only.
            </p>
          </div>
          <div className="step">
            <h3>Get matched with a peer</h3>
            <p>
              You are paired with another verified student. You each choose a nickname; real names
              stay private.
            </p>
          </div>
          <div className="step">
            <h3>Message when you can</h3>
            <p>
              It is asynchronous, like a pen pal. Reply on your own time — a slow reply is normal,
              not rejection.
            </p>
          </div>
          <div className="step">
            <h3>Check in over the term</h3>
            <p>
              A fresh pairing each week if you would like one. And when you feel better, you can
              leave well.
            </p>
          </div>
        </div>
      </section>

      {/* SAFETY — consolidated once (previously repeated across the kicker,
          the reassurance row, two panels and the footer). */}
      <section className="safe">
        <div className="safe-inner">
          <div>
            <span className="kicker">Built for trust</span>
            <h2>
              Safe <em>by design</em> — not for engagement.
            </h2>
          </div>
          <ul className="safe-list">
            <li>
              <span className="mk" aria-hidden="true">
                ✦
              </span>
              <span>Every conversation is moderated by a real team.</span>
            </li>
            <li>
              <span className="mk" aria-hidden="true">
                ✦
              </span>
              <span>Adults 18 and older only, verified at sign-up.</span>
            </li>
            <li>
              <span className="mk" aria-hidden="true">
                ✦
              </span>
              <span>You're a nickname to your match — never your real name or email.</span>
            </li>
            <li>
              <span className="mk" aria-hidden="true">
                ✦
              </span>
              <span>
                Not therapy or a crisis line — {CRISIS_RESOURCES[0].name} is always one tap away.
              </span>
            </li>
          </ul>
        </div>
      </section>

      <section className="lcta">
        <h2>Reaching out is the hard part. We kept the rest simple.</h2>
        <p>Free for students. One email, one real person, one conversation at a time.</p>
        <button type="button" onClick={focusStart}>
          Start with your university email
        </button>
      </section>

      {/* A single ✦ before the footer — the only divider, reference-style. */}
      <p className="fleuron" aria-hidden="true">
        ✦
      </p>

      <footer className="lfooter">
        <div className="lfooter-inner">
          <span className="wordmark lfooter-mark">
            <em className="wordmark-accent">Less</em>Alone
          </span>
          <p className="lfooter-tagline">
            <em>Made with care, for students who feel alone.</em>
          </p>
          <p className="lfooter-disclaimer">
            A moderated, text-based peer-connection community. Not a substitute for professional
            care — in a crisis, call or text <strong>988</strong>.
          </p>
          <nav className="lfooter-links" aria-label="About">
            <a href="/safety">Safety</a>
            <a href="/guidelines">Guidelines</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </nav>
          <p className="lfooter-mark-bottom" aria-hidden="true">
            <span>✦</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
